import {
    createReducer,
    createAction,
    createAsyncThunk,
} from '@reduxjs/toolkit';

import { LOCALSTORAGE } from 'config';
import {
    AuthManager,
    SelfManager,
    OrganisationMin,
    Loadable,
    isErrorResp,
    ErrorTypes,
} from 'models';
import { makeThunkFromAPICall } from 'state';

export type AuthState = {
    tokens: Loadable<AuthManager.OAuth2Creds>;
    user: Loadable<SelfManager.User>;
    organisations: Loadable<OrganisationMin[]>;
    organisation?: OrganisationMin | null;
    updatePasswordResponse?: Loadable<SelfManager.API.CommandResponse>;
    updateEmailPreferenceResponse?: Loadable<SelfManager.API.CommandResponse>;
};

const initialState: AuthState = {
    tokens: { status: 'idle' },
    user: { status: 'idle' },
    organisations: { status: 'idle' },
    organisation: null,
    updatePasswordResponse: { status: 'idle' },
    updateEmailPreferenceResponse: { status: 'idle' },
};

export const setTokensStatus = createAction<Loadable<AuthManager.OAuth2Creds>>(
    'auth/set-tokens-status',
);

export const setOrganisation = createAction<OrganisationMin | null>(
    'auth/set-organisation',
);

export const setTokens =
    createAction<AuthManager.OAuth2Creds>('auth/set-tokens');

export const [getSelf, setUser] = makeThunkFromAPICall(
    SelfManager.getSelf,
    'auth/get-self',
);

export const [sendUpdatePassword, setUpdatePasswordResponse] =
    makeThunkFromAPICall(SelfManager.updatePassword, 'self/set-password');

export const [sendUpdateEmailPreference, setUpdateEmailPreferenceResponse] =
    makeThunkFromAPICall(
        SelfManager.updateEmailPreference,
        'self/set-email-preference',
    );

export const getToken = createAsyncThunk(
    'auth/get-token',
    async (
        payload: {
            authCode?: string;
            tokens?: AuthManager.OAuth2Creds | null;
        },
        thunk,
    ) => {
        thunk.dispatch(setTokensStatus({ status: 'loading' }));
        if (payload.authCode || payload.tokens?.refreshToken) {
            try {
                const creds = await AuthManager.getToken(payload);
                if (!isErrorResp(creds)) {
                    thunk.dispatch(setTokens(creds));
                    thunk.dispatch(
                        setTokensStatus({
                            status: 'finished',
                        }),
                    );
                    localStorage.setItem(
                        LOCALSTORAGE.AUTH,
                        JSON.stringify(creds),
                    );
                    return creds;
                }
                thunk.dispatch(
                    setTokensStatus({
                        error: creds,
                        status: 'error',
                    }),
                );
            } catch (err: any) {
                thunk.dispatch(
                    setTokensStatus({
                        error: err,
                        status: 'error',
                    }),
                );
            }
        } else {
            thunk.dispatch(
                setTokensStatus({
                    error: {
                        error: 'No credentials provided',
                        code: ErrorTypes.INVALID_CREDENTIALS,
                    },
                    status: 'error',
                }),
            );
        }
        return null;
    },
);

export const authReducer = createReducer(initialState, (builder) => {
    builder.addCase(setTokensStatus, (state, action) => {
        return { ...state, tokens: { ...state.tokens, ...action.payload } };
    });

    builder.addCase(setTokens, (state, action) => {
        return {
            ...state,
            tokens: { ...state.tokens, object: action.payload },
        };
    });

    builder.addCase(setOrganisation, (state, action) => {
        return {
            ...state,
            organisation: action.payload,
        };
    });

    builder.addCase(setUser, (state, action) => {
        return {
            ...state,
            user: { ...state.user, ...action.payload.loadable },
            // If we load the user and we only get 1 organisation,
            // select that one by default
            organisation:
                action.payload.loadable.object?.organisation_set &&
                action.payload.loadable.object.organisation_set.length === 1 &&
                !state.organisation
                    ? action.payload.loadable.object.organisation_set[0]
                    : state.organisation,
        };
    });

    builder.addCase(setUpdatePasswordResponse, (state, action) => {
        return {
            ...state,
            updatePasswordResponse: action.payload.loadable,
        };
    });

    builder.addCase(setUpdateEmailPreferenceResponse, (state, action) => {
        return {
            ...state,
            updateEmailPreferenceResponse: action.payload.loadable,
        };
    });
});

export const AuthActions = {
    setTokens,
    setTokensStatus,
    setOrganisation,
    setUser,
    getSelf,
    getToken,
    sendUpdatePassword,
    sendUpdateEmailPreference,
};

export default authReducer;
